<template>
  <v-chart
    :options="chartOption"
    :autoresize="true"
    :class="[{ default: defaultChart }]"
    v-loading="isLoading"
  />
</template>

<script>
import ECharts from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/chart/line";
import "echarts/lib/component/polar";
import "echarts/lib/chart/map";
import "echarts/lib/chart/radar";
import "echarts/lib/chart/scatter";
import "echarts/lib/chart/effectScatter";
import "echarts/lib/component/polar";
import "echarts/lib/component/geo";
import "echarts/lib/component/visualMap";
import "echarts/lib/component/dataset";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/title";
import "echarts/map/js/world";

import cloneDeep from "lodash.clonedeep";
import variables from "@/styles/variables.scss";

import {
  bar,
  barX,
  basic,
  pie,
  pieRose,
  doughnut,
  polar,
  scatter
} from "../templates/templates.js";

export default {
  components: {
    "v-chart": ECharts
  },
  props: ["chart-type", "id"],
  data() {
    return {
      isLoading: false,
      defaultChart: true,
      chartData: [],
      chartFields: [],
      chartOption: null,
      chartStyles: [],
      styleUpdate: false,
      bar,
      basic,
      pie,
      pieRose,
      doughnut,
      polar,
      barX,
      scatter
    };
  },
  watch: {
    "$store.state.charts.chartsData": {
      handler: function(value) {
        const chartData = value[this.id];
        if (chartData) {
          if (!chartData.updated) {
            return;
          }
          this.chartData = chartData;
          this.buildChart();
          chartData.updated = false;
        }
      },
      deep: true,
      immediate: true
    },
    "$store.state.charts.openedCharts": {
      handler: function(value) {
        const currentChartOptions = value[this.id];
        if (currentChartOptions.source) {
          if (!currentChartOptions.updated) {
            return;
          }
          if (currentChartOptions.updated === "style") {
            this.styleUpdate = true;
            this.buildChart();
            return;
          }
          this.getChartData(currentChartOptions.source);
          currentChartOptions.updated = false;
        }
      },
      deep: true,
      immediate: true
    },
    chartType: {
      handler: function() {
        const chartData = this.$store.state.charts.chartsData[this.id];
        if (chartData) {
          this.chartData = chartData;
        }
        this.buildChart();
      },
      deep: true,
      immediate: true
    },
    "$store.state.charts.activeChart": {
      handler: function(value) {
        if (Object.keys(value)[0] === this.id) {
          this.defaultChart = false;
        } else {
          if (this.$store.state.charts.chartsData[this.id]) {
            return;
          }
          this.defaultChart = true;
        }
      },
      deep: true,
      immediate: true
    },
    theme() {
      this.buildChart();
    }
  },
  computed: {
    theme() {
      return this.$store.state.theme;
    }
  },
  methods: {
    buildChart() {
      !this.styleUpdate ? (this.isLoading = true) : (this.styleUpdate = false);
      // TODO: refact to vue-components
      const chartTitle = this.chartData.title;

      this.chartFields = this.chartData.fields;
      this.chartStyles = this.$store.state.charts.openedCharts[this.id].styles;
      switch (this.chartType) {
        case "bar":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartHeader = ["#"];
            const newChartData = [chartHeader];
            const chartSeries = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              chartHeader.push(e.dataTitle);
              chartSeries.push({
                type: "bar",
                color: this.chartStyles[i].background
              });
            });
            for (let i = 0; i < this.chartFields.length; i++) {
              const chartField = [this.chartFields[i]];

              this.chartData.data.forEach(e => {
                chartField.push(e.data[i]);
              });
              newChartData.push(chartField);
            }
            newData = cloneDeep(this.bar);
            newData.dataset.source = newChartData;
            newData.series = chartSeries;
            if (newChartData.length > 50) {
              newData.dataZoom = [
                {
                  type: "slider",
                  show: true,
                  realtime: true,
                  start: 0,
                  end: 30
                }
              ];
            }
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.bar);
          }
          break;
        case "bar-x":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartLegend = [];
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              chartLegend.push(e.dataTitle);
              chartData.push({
                name: e.dataTitle,
                type: "bar",
                stack: "one",
                data: e.data,
                color: this.chartStyles[i].background
              });
            });
            newData = cloneDeep(this.barX);
            newData.yAxis.data = this.chartFields;
            newData.legend.data = chartLegend;
            newData.series = chartData;
            if (chartData[0].data.length > 50) {
              newData.dataZoom = [
                {
                  type: "slider",
                  yAxisIndex: 0,
                  realtime: true,
                  start: 0,
                  end: 30
                }
              ];
            }
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.barX);
          }
          break;
        case "basic":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartLegend = [];
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              chartLegend.push(e.dataTitle);
              chartData.push({
                name: e.dataTitle,
                type: "line",
                areaStyle: {
                  color: this.chartStyles[i].background
                },
                smooth: true,
                data: e.data,
                lineStyle: {
                  width: this.chartStyles[i].lineWidth,
                  color: this.chartStyles[i].border
                },
                itemStyle: {
                  color: this.chartStyles[i].point,
                  borderColor: this.chartStyles[i].pointBorder
                },
                symbol: "circle"
              });
            });
            newData = cloneDeep(this.basic);
            newData.legend.data = chartLegend;
            newData.series = chartData;
            newData.xAxis[0].data = this.chartFields;
            if (chartData[0].data.length > 50) {
              newData.dataZoom = [
                {
                  type: "slider",
                  show: true,
                  realtime: true,
                  start: 0,
                  end: 30
                }
              ];
            }
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.basic);
          }
          break;
        case "pie":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              let dataSum = e.data.reduce((sum, current) => {
                return sum + current;
              }, 0);

              chartData.push({
                name: e.dataTitle,
                value: dataSum,
                itemStyle: { color: this.chartStyles[i].background }
              });
            });
            newData = cloneDeep(this.pie);
            newData.series[0].data = chartData;
            newData.series[0].name = chartTitle;
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.pie);
          }
          break;
        case "doughnut":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartLegend = [];
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              let dataSum = e.data.reduce((sum, current) => {
                return sum + current;
              }, 0);

              chartLegend.push(e.dataTitle);
              chartData.push({
                name: e.dataTitle,
                value: dataSum,
                itemStyle: { color: this.chartStyles[i].background }
              });
            });
            newData = cloneDeep(this.doughnut);
            newData.legend.data = chartLegend;
            newData.series[0].data = chartData;
            newData.series[0].name = chartTitle;
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.doughnut);
          }
          break;
        case "pie-rose":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              let dataSum = e.data.reduce((sum, current) => {
                return sum + current;
              }, 0);

              chartData.push({
                value: dataSum,
                name: e.dataTitle,
                itemStyle: {
                  color: this.chartStyles[i].background
                }
              });
            });
            chartData.sort(function(a, b) {
              return a.value - b.value;
            });
            newData = cloneDeep(this.pieRose);
            newData.series[0].name = chartTitle;
            newData.series[0].data = chartData;
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.pieRose);
          }
          break;
        case "polar":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartLegend = [];
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              let polarChartData = [];

              for (let i = 0; i < e.data.length; i++) {
                polarChartData.push([e.data[i], i]);
              }
              chartLegend.push(e.dataTitle);
              chartData.push({
                coordinateSystem: "polar",
                name: e.dataTitle,
                type: "line",
                data: polarChartData,
                lineStyle: {
                  width: this.chartStyles[i].lineWidth,
                  color: this.chartStyles[i].border
                },
                itemStyle: {
                  color: this.chartStyles[i].point,
                  borderColor: this.chartStyles[i].pointBorder
                }
              });
            });
            newData = cloneDeep(this.polar);
            newData.legend.data = chartLegend;
            newData.series = chartData;
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.polar);
          }
          break;
        case "scatter":
          if (this.chartData.fields) {
            this.defaultChart = false;
            const chartLegend = [];
            const chartData = [];
            let newData = {};

            this.chartData.data.forEach((e, i) => {
              let newChartData = [];

              for (let i = 0; i < e.data.length; i++) {
                newChartData.push([this.chartFields[i], e.data[i]]);
              }
              chartLegend.push(e.dataTitle);
              chartData.push({
                type: "scatter",
                name: e.dataTitle,
                symbolSize: 20,
                data: newChartData,
                itemStyle: {
                  shadowBlur: 5,
                  shadowOffsetX: 0,
                  shadowColor: "rgba(200, 200, 200, 0.8)",
                  color: this.chartStyles[i].background
                }
              });
            });
            newData = cloneDeep(this.scatter);
            newData.legend.data = chartLegend;
            newData.series = chartData;
            this.chartOption = cloneDeep(newData);
          } else {
            this.chartOption = cloneDeep(this.scatter);
          }
          break;
        default:
          break;
      }
      this.setColorTheme();
      this.isLoading = false;
    },
    setColorTheme() {
      const colors = {};

      if (this.theme === "dark-theme") {
        colors.bg = variables.primaryBgDt;
        colors.bgRevers = variables.primaryBgLt;
        colors.text = variables.regularTextDt;
      } else {
        colors.bg = variables.primaryBgLt;
        colors.bgRevers = variables.primaryBgDt;
        colors.text = variables.regularTextLt;
      }

      this.chartOption.toolbox.feature = {
        saveAsImage: {
          title: "PNG",
          backgroundColor: colors.bg
        }
      };
      this.chartOption.textStyle = { color: colors.text };
      if (this.chartOption.legend)
        this.chartOption.legend.textStyle = { color: colors.text };
      if (this.chartOption.dataZoom) {
        this.chartOption.dataZoom[0] = {
          type: "slider",
          show: true,
          realtime: true,
          start: 0,
          end: 30,
          dataBackground: {
            lineStyle: { color: colors.text },
            areaStyle: { color: `${colors.bgRevers}4d` }
          },
          textStyle: {
            color: colors.text
          }
        };
        if (this.chartType === "bar-x")
          this.chartOption.dataZoom[0].yAxisIndex = 0;
      }
    },
    async getChartData(options) {
      if (!this.styleUpdate) this.isLoading = true;
      const source_id = options.source_id;
      const id = options.id;
      const chartId = options.chartId;
      const chartName = options.chartName;
      const xAxis = options.xAxis;
      const yAxis = options.yAxis;

      const url = `objectInfo/${source_id}?config=${JSON.stringify({
        limit: 5000
      })}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      let xData = [];
      let fieldsData = [];
      let currentData = data;

      for (let key in currentData) {
        let thisField = currentData[key];
        xData.push(thisField[xAxis]);
      }
      yAxis.forEach(e => {
        let fieldData = [];
        for (let key in currentData) {
          let field = currentData[key];
          if (!field[e]) {
            fieldData.push(0);
          } else {
            fieldData.push(field[e]);
          }
        }
        fieldsData.push({
          data: fieldData,
          dataTitle: e
        });
      });
      this.$store.commit("ADD_CHART_DATA", {
        title: chartName,
        xTitle: xAxis,
        column: xData,
        source_id: source_id,
        id,
        fieldsData,
        chartId
      });
    }
  },
  async created() {
    const currentChartOptions = this.$store.state.charts.openedCharts[this.id];
    if (currentChartOptions.source)
      this.getChartData(currentChartOptions.source);

    const chartData = this.$store.state.charts.chartsData[this.id];
    if (chartData) this.chartData = chartData;

    this.buildChart();
  }
};
</script>

<style lang="scss">
.echarts {
  width: 100%;
  height: 100%;
}
</style>
